import css from './style.module.scss';
import Checkboxes from '../Checkboxes';
import Button from '../Button';
import Range from '../Range';
import { useEffect, useMemo, useRef, useState } from 'react';
import Modal from '../Modal';
import { gql } from '../../util/fetchGql';
import { Emit, Events, Listen } from '../../util/Events';
import { useRouter } from 'next/router';

export const PRICE_BY_ID = {
	1: { min: null, max: 100 },// - Up to £100
	2: { min: 100, max: 500 },// - £100 to £500
	3: { min: 500, max: 1000 },// - £500 to £1,000
	4: { min: 1000, max: 5000 },// - £1,000 to £5,000
	5: { min: 5000, max: 10000 },// - £5,000 to £10,000
	6: { min: 10000, max: 25000 },// - £10,000 to £25,000
	7: { min: 25000, max: 50000 },// - £25,000 to £50,000
	8: { min: 50000, max: null },// - £50,000 +
};

const PRICE_OPTIONS = [
	{ label: 'Up to £100', value: 1 },
	{ label: '£100 to £500', value: 2 },
	{ label: '£500 to £1,000', value: 3 },
	{ label: '£1,000 to £5,000', value: 4 },
	{ label: '£5,000 to £10,000', value: 5 },
	{ label: '£10,000 to £25,000', value: 6 },
	{ label: '£25,000 to £50,000', value: 7 },
	{ label: '£50,000 +', value: 8 },
];

export const FILTER_QUERY = gql`
	query Filter {
		productCategories: entries (
				section: "productCategory" 
				orderBy: "title asc"
		) {
			value: id
			label: title
		}
		brands: entries (
			section: "brand" 
			orderBy: "title asc"
		) {
			value: id
			label: title
		}
        paymentOptions: tags (group: "paymentOption") {
			value: id
			label: title
		}
        conditions: tags (group: "condition") {
			value: id
			label: title
		}
		productCheapest: product (orderBy: "defaultPrice asc") {
			price: defaultPrice
		}
		productExpensive: product (orderBy: "defaultPrice desc") {
            price: defaultPrice
		}
	}
`;

export const PRODUCT_FILTER_META_FRAGMENT = gql`
	fragment ProductFilterMeta on products_Product {
        filter_productCategories: productCategories { id }
		filter_brands: brands { id }
		filter_defaultPrice: defaultPrice
		filter_paymentOptions: paymentOptions { id }
		filter_conditions: condition { id }
	}
`;

const contains = (a, b) => a.some(element => {
	return b.includes(element);
});

const filterOut = (products, filterValues, ignore = null) => {
	const keys = Object.keys(filterValues);

	if (ignore && keys.indexOf(ignore) > -1)
		delete keys[keys.indexOf(ignore)];

	return [...products].filter(product => {
		for (const key of keys) {
			const values = filterValues[key];
			if (!key || !values || values.length === 0 || key === 'sort') continue;

			if (key === 'prices') {
				let inAnyRange = false;

				for (const id of values) {
					const { min, max } = PRICE_BY_ID[id];
					const p = product.filter_defaultPrice;

					if (
						(min === null || p > min) &&
						(max === null || p < max)
					) {
						inAnyRange = true;
						break;
					}
				}

				if (!inAnyRange) return false;
			} else if (!contains(product[`filter_${key}`].map(c => +c.id), values)) {
				return false;
			}
		}

		return true;
	});
};

const Section = ({ heading, action = null, children }) => (
	<div className={css.section}>
		<header>
			<h4>{heading}</h4>
			{action}
		</header>
		{children}
	</div>
);

export default function Filter ({
	products,
	filterOptions,
	hideBrands,
	hideCategories,
	setFilteredProducts,
}) {
	const toggleRef = useRef();
	const [isMobile, setIsMobile] = useState(false)
		, [isFilterOpen, setIsFilterOpen] = useState(false)
		, [hasChecked, setHasChecked] = useState({})
		, [filterValues, setFilterValues] = useState({})
		, [count, setCount] = useState(products.length);

	useEffect(() => {
		if (!toggleRef.current)
			return;

		const io = new IntersectionObserver(entries => {
			entries?.[0]?.isIntersecting && setIsMobile(true);
		});

		io.observe(toggleRef.current);

		return () => {
			io.disconnect();
		};
	}, [toggleRef]);

	useEffect(() => Listen(Events.CheckboxesChange, (name, values) => {
		setFilterValues(prev => ({
			...prev,
			[name]: values,
		}));

		setHasChecked(prev => ({
			...prev,
			[name]: values.length > 0,
		}));
	}), []);

	const opts = useMemo(() => {
		const opts = {
			categories: filterOptions.productCategories ?? [],
			brands: filterOptions.brands ?? [],
			prices: PRICE_OPTIONS,
			paymentOptions: filterOptions.paymentOptions ?? [],
			conditions: filterOptions.conditions ?? [],
		};

		// 1. Filter out unavailable categories
		opts.categories = opts.categories.filter(cat => {
			cat.isEmpty = false;

			for (const product of filterOut(products, filterValues, 'productCategories'))
				if (product.filter_productCategories.map(c => c.id).indexOf(cat.value) > -1)
					return true;

			if ((filterValues?.productCategories?.indexOf(cat.value) ?? -1) > -1) {
				cat.isEmpty = true;
				return true;
			}

			return false;
		});

		// 2. Filter out unavailable brands
		opts.brands = opts.brands.filter(cat => {
			cat.isEmpty = false;

			for (const product of filterOut(products, filterValues, 'brands'))
				if (product.filter_brands.map(c => c.id).indexOf(cat.value) > -1)
					return true;

			if ((filterValues?.brands?.indexOf(+cat.value) ?? -1) > -1) {
				cat.isEmpty = true;
				return true;
			}

			return false;
		});

		// 3. Filter out unavailable price ranges
		opts.prices = opts.prices.filter(price => {
			price.isEmpty = false;
			const { min, max } = PRICE_BY_ID[price.value];

			for (const product of filterOut(products, filterValues, 'prices'))
				if (
					(min === null || product.filter_defaultPrice >= min) &&
					(max === null || product.filter_defaultPrice <= max)
				) return true;

			if ((filterValues?.price?.indexOf(price.value) ?? -1) > -1) {
				price.isEmpty = true;
				return true;
			}

			return false;
		});

		// 4. Filter out unavailable payment options
		opts.paymentOptions = opts.paymentOptions.filter(opt => {
			opt.isEmpty = false;

			for (const product of filterOut(products, filterValues, 'paymentOptions'))
				if (product.filter_paymentOptions.map(o => o.id).indexOf(opt.value) > -1)
					return true;

			if ((filterValues?.paymentOptions?.indexOf(opt.value) ?? -1) > -1) {
				opt.isEmpty = true;
				return true;
			}

			return false;
		});

		// 5. Filter out unavailable conditions
		opts.conditions = opts.conditions.filter(con => {
			con.isEmpty = false;

			for (const product of filterOut(products, filterValues, 'conditions'))
				if (product.filter_conditions.map(c => c.id).indexOf(con.value) > -1)
					return true;

			if ((filterValues?.conditions?.indexOf(con.value) ?? -1) > -1) {
				con.isEmpty = true;
				return true;
			}

			return false;
		});

		return opts;
	}, [products, filterOptions, filterValues]);

	useEffect(() => {
		const filteredProducts = filterOut(products, filterValues).sort((a, b) => {
			switch (filterValues?.sort?.[0]) {
				case 'high': return b.filter_defaultPrice - a.filter_defaultPrice;
				default:
				case 'low': return a.filter_defaultPrice - b.filter_defaultPrice;
			}
		});
		setFilteredProducts(filteredProducts);
		setCount(filteredProducts.length);
	}, [products, filterValues]);

	const onShowFilterClick = () => setIsFilterOpen(true)
		, onOverlayClick = () => setIsFilterOpen(false)
		, onClearClick = name => () => Emit(Events.ClearCheckboxes, name);

	const filter = (
		<aside className={css.filter}>
			{!hideCategories && (opts.productCategories?.length ?? 0) > 0 && (
				<Section
					heading="Categories"
					action={hasChecked?.productCategories ? (
						<Button
							text small blue
							onClick={onClearClick('productCategories')}
						>
							Clear
						</Button>
					): null}
				>
					<Checkboxes
						name="productCategories"
						label="categories"
						options={opts.productCategories}
						checked={filterValues.productCategories}
					/>
				</Section>
			)}

			{!hideBrands && (opts.brands?.length ?? 0) > 0 && (
				<Section
					heading="Brands"
					action={hasChecked?.brands ? (
						<Button
							text small blue
							onClick={onClearClick('brands')}
						>
							Clear
						</Button>
					): null}
				>
					<Checkboxes
						name="brands"
						label="brands"
						options={opts.brands}
						checked={filterValues.brands}
						defaultShowAll
					/>
				</Section>
			)}

			{opts.prices.length > 0 && (
				<Section heading="Price">
					<Checkboxes
						name="prices"
						label="price"
						overflowLimit={8}
						options={opts.prices}
						checked={filterValues.price}
					/>
				</Section>
			)}

			{opts.paymentOptions.length > 0 && (
				<Section
					heading="Payment Options"
					action={hasChecked?.paymentOptions ? (
						<Button
							text small blue
							onClick={onClearClick('paymentOptions')}
						>
							Clear
						</Button>
					): null}
				>
					<Checkboxes
						name="paymentOptions"
						label="payment options"
						options={opts.paymentOptions}
						checked={filterValues.paymentOptions}
					/>
				</Section>
			)}

			{opts.conditions.length > 0 && (
				<Section
					heading="Condition"
					action={hasChecked?.conditions ? (
						<Button
							text small blue
							onClick={onClearClick('conditions')}
						>
							Clear
						</Button>
					): null}
				>
					<Checkboxes
						name="conditions"
						label="conditions"
						options={opts.conditions}
						checked={filterValues.conditions}
					/>
				</Section>
			)}
		</aside>
	);

	return (
		<>
			<label className={css.sort}>
				<span>Sort</span>
				<select
					name="sort"
					onChange={e => Emit(Events.CheckboxesChange, 'sort', [e.target.value])}
				>
					{/*<option value="new">Newest</option>*/}
					<option value="low">Price: Low - High</option>
					<option value="high">Price: High - Low</option>
				</select>
			</label>

			<div className={css.toggleFilter} ref={toggleRef}>
				<Button rounded grey slim onClick={onShowFilterClick}>Filter Results</Button>
			</div>

			<span className={css.count}>
				{count} Product{count === 1 ? '' : 's'}
			</span>

			{isMobile ? (
				<Modal isOpen={isFilterOpen} onRequestClose={onOverlayClick} blue>
					{filter}
				</Modal>
			) : filter}
		</>
	);
}
